import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Clock, Award, AlertTriangle, CheckCircle, ArrowLeft,
  ArrowRight, Flag, BookOpen, X
} from 'lucide-react';
import * as api from '../../api/educentialApi';
import './QuizTaking.css';

function QuizTaking() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadQuizAndStart();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [quizId]);

  useEffect(() => {
    if (quiz && quiz.timeLimit && !result) {
      setTimeRemaining(quiz.timeLimit * 60); // Convert to seconds
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [quiz, result]);

  const loadQuizAndStart = async () => {
    try {
      setLoading(true);

      // Load quiz without answers
      const quizResponse = await api.getQuiz(quizId, false);
      if (!quizResponse.success) {
        throw new Error('Failed to load quiz');
      }

      const quizData = quizResponse.data;
      setQuiz(quizData);

      // Start new attempt
      const attemptResponse = await api.startQuiz(quizId);
      if (!attemptResponse.success) {
        throw new Error(attemptResponse.error || 'Failed to start quiz');
      }

      setAttempt(attemptResponse.data);

      // Initialize answers object
      const initialAnswers = {};
      quizData.Questions.forEach(q => {
        initialAnswers[q.id] = '';
      });
      setAnswers(initialAnswers);

    } catch (error) {
      console.error('Load quiz error:', error);
      alert(error.message || 'Failed to load quiz. Please try again.');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < quiz.Questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleQuestionJump = (index) => {
    setCurrentQuestionIndex(index);
  };

  const handleAutoSubmit = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    await submitQuiz();
  };

  const handleSubmitClick = () => {
    const unanswered = quiz.Questions.filter(q => !answers[q.id] || answers[q.id] === '').length;
    if (unanswered > 0) {
      setShowSubmitConfirm(true);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    try {
      setSubmitting(true);
      setShowSubmitConfirm(false);

      // Prepare answers for submission
      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        questionId: parseInt(questionId),
        answer: answer.toString()
      }));

      const response = await api.submitQuiz(attempt.id, formattedAnswers);
      if (response.success) {
        setResult(response.data);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
    } catch (error) {
      console.error('Submit quiz error:', error);
      alert('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.values(answers).filter(a => a !== '').length;
  };

  if (loading) {
    return (
      <div className="quiz-taking-container">
        <div className="loading-state">Loading quiz...</div>
      </div>
    );
  }

  // Show results if quiz submitted
  if (result) {
    return <QuizResults result={result} quiz={quiz} onClose={() => navigate(-1)} />;
  }

  const currentQuestion = quiz.Questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quiz.Questions.length) * 100;

  return (
    <div className="quiz-taking-container">
      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Submit Quiz?</h3>
            <p>
              You have {quiz.Questions.length - getAnsweredCount()} unanswered question(s).
              Are you sure you want to submit?
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowSubmitConfirm(false)}>
                Review Answers
              </button>
              <button className="btn-confirm" onClick={submitQuiz}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="quiz-header">
        <div className="quiz-header-left">
          <button className="btn-exit" onClick={() => {
            if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
              navigate(-1);
            }
          }}>
            <X className="w-5 h-5" />
          </button>
          <div>
            <h1>{quiz.title}</h1>
            <p className="quiz-meta">
              {quiz.Questions.length} Questions • {quiz.passingScore}% to Pass
            </p>
          </div>
        </div>
        <div className="quiz-header-right">
          {quiz.timeLimit && (
            <div className={`timer ${timeRemaining < 300 ? 'timer-warning' : ''}`}>
              <Clock className="w-5 h-5" />
              <span>{formatTime(timeRemaining)}</span>
            </div>
          )}
          <button
            className="btn-submit-header"
            onClick={handleSubmitClick}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Quiz'}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="quiz-content">
        {/* Question Navigator Sidebar */}
        <div className="question-navigator">
          <h3>Questions</h3>
          <div className="question-grid">
            {quiz.Questions.map((q, index) => (
              <button
                key={q.id}
                className={`question-nav-btn ${index === currentQuestionIndex ? 'active' : ''} ${answers[q.id] ? 'answered' : ''}`}
                onClick={() => handleQuestionJump(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <div className="nav-legend">
            <div className="legend-item">
              <span className="legend-dot answered"></span>
              <span>Answered ({getAnsweredCount()})</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot unanswered"></span>
              <span>Unanswered ({quiz.Questions.length - getAnsweredCount()})</span>
            </div>
          </div>
        </div>

        {/* Main Question Area */}
        <div className="question-area">
          <div className="question-header-info">
            <span className="question-number">Question {currentQuestionIndex + 1} of {quiz.Questions.length}</span>
            <span className="question-points">{currentQuestion.points} {currentQuestion.points === 1 ? 'point' : 'points'}</span>
          </div>

          <h2 className="question-text">{currentQuestion.question}</h2>

          {/* Answer Options */}
          <div className="answer-options">
            {currentQuestion.type === 'MULTIPLE_CHOICE' && (
              <div className="mc-options">
                {currentQuestion.Options.map((option, index) => (
                  <label key={option.id} className="option-label">
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value={option.id}
                      checked={answers[currentQuestion.id] === option.id.toString()}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    />
                    <span className="option-text">
                      <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                      {option.text}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {currentQuestion.type === 'TRUE_FALSE' && (
              <div className="tf-options">
                <label className="option-label">
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value="true"
                    checked={answers[currentQuestion.id] === 'true'}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  />
                  <span className="option-text">True</span>
                </label>
                <label className="option-label">
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value="false"
                    checked={answers[currentQuestion.id] === 'false'}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  />
                  <span className="option-text">False</span>
                </label>
              </div>
            )}

            {currentQuestion.type === 'SHORT_ANSWER' && (
              <div className="sa-input">
                <input
                  type="text"
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  placeholder="Type your answer here..."
                  className="answer-input"
                />
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="question-nav-buttons">
            <button
              className="btn-nav"
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>
            {currentQuestionIndex === quiz.Questions.length - 1 ? (
              <button
                className="btn-submit"
                onClick={handleSubmitClick}
                disabled={submitting}
              >
                <Flag className="w-5 h-5" />
                <span>{submitting ? 'Submitting...' : 'Submit Quiz'}</span>
              </button>
            ) : (
              <button
                className="btn-nav btn-next"
                onClick={handleNextQuestion}
              >
                <span>Next</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Quiz Results Component
function QuizResults({ result, quiz, onClose }) {
  const passed = result.passed;

  return (
    <div className="quiz-results-container">
      <div className="results-header">
        <div className={`results-icon ${passed ? 'passed' : 'failed'}`}>
          {passed ? <CheckCircle className="w-16 h-16" /> : <AlertTriangle className="w-16 h-16" />}
        </div>
        <h1>{passed ? 'Congratulations!' : 'Keep Practicing'}</h1>
        <p className="results-subtitle">
          {passed ? 'You passed the quiz!' : 'You didn\'t pass this time, but you can try again.'}
        </p>
      </div>

      <div className="results-stats">
        <div className="stat-card">
          <Award className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Score</span>
            <span className="stat-value">{result.score}%</span>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Points</span>
            <span className="stat-value">{result.totalPoints}/{result.maxPoints}</span>
          </div>
        </div>
        <div className="stat-card">
          <Clock className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Time</span>
            <span className="stat-value">{Math.floor(result.timeSpent / 60)}m {result.timeSpent % 60}s</span>
          </div>
        </div>
        <div className="stat-card">
          <Flag className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Status</span>
            <span className={`stat-value ${passed ? 'passed' : 'failed'}`}>
              {passed ? 'Passed' : 'Failed'}
            </span>
          </div>
        </div>
      </div>

      {quiz.showAnswers && (
        <div className="answers-review">
          <h2>Review Answers</h2>
          {result.Answers.map((answer, index) => (
            <div key={answer.id} className={`answer-card ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
              <div className="answer-header">
                <span className="question-num">Question {index + 1}</span>
                <span className={`answer-status ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                  {answer.isCorrect ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {answer.isCorrect ? 'Correct' : 'Incorrect'}
                </span>
              </div>
              <p className="question-text">{answer.Question.question}</p>
              <div className="answer-details">
                <p><strong>Your answer:</strong> {
                  answer.Question.type === 'MULTIPLE_CHOICE'
                    ? answer.Question.Options.find(o => o.id === parseInt(answer.answer))?.text
                    : answer.answer
                }</p>
                {!answer.isCorrect && (
                  <p className="correct-answer">
                    <strong>Correct answer:</strong> {
                      answer.Question.type === 'MULTIPLE_CHOICE'
                        ? answer.Question.Options.find(o => o.isCorrect)?.text
                        : answer.Question.correctAnswer
                    }
                  </p>
                )}
                {answer.Question.explanation && (
                  <p className="explanation">
                    <BookOpen className="w-4 h-4 inline" /> <strong>Explanation:</strong> {answer.Question.explanation}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="results-actions">
        <button className="btn-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export default QuizTaking;
